import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Play, X, CreditCard, Users, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
  return match ? match[1] : null;
};

// Helper to get YouTube thumbnail
const getYouTubeThumbnail = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
};

// Helper to get embed URL
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const ytId = getYouTubeVideoId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`;
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  
  // Direct video URL
  return url;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [appSettings, setAppSettings] = useState({
    app_name: 'Thrive',
    app_tagline: 'Beekeeping Management',
    app_description: '',
    app_logo: '',
    contact_email: '',
    logo_max_height: 64,
    logo_shape: 'square',
  });
  const [demoVideo, setDemoVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { login, enterDemoMode } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any stale crew token when arriving at owner login
    localStorage.removeItem('crewToken');
    
    // Fetch public app settings, active demo video, and CAPTCHA
    Promise.all([
      fetch(`${API}/api/app-settings/public`).then(res => res.json()).catch(() => ({})),
      fetch(`${API}/api/demo-videos/active`).then(res => res.json()).catch(() => ({ video: null }))
    ]).then(([settings, videoData]) => {
      if (settings && typeof settings === 'object' && !settings.error) setAppSettings(prev => ({ ...prev, ...settings }));
      if (videoData.video) setDemoVideo(videoData.video);
    });
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    try {
      const res = await fetch(`${API}/api/auth/captcha`);
      const data = await res.json();
      setCaptchaToken(data.token);
      setCaptchaQuestion(data.question);
      setCaptchaAnswer('');
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    if (!captchaAnswer.trim()) {
      toast.error('Please solve the CAPTCHA');
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password, captchaToken, captchaAnswer);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.message);
      loadCaptcha(); // Refresh CAPTCHA on failure
    } finally {
      setLoading(false);
    }
  };

  const handleDemoMode = () => {
    enterDemoMode();
    toast.info('Viewing demo data (read-only)');
    navigate('/');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setForgotSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const thumbnail = demoVideo?.thumbnail_url || getYouTubeThumbnail(demoVideo?.video_url);
  const embedUrl = getEmbedUrl(demoVideo?.video_url);
  const isDirectVideo = demoVideo?.video_url && !getYouTubeVideoId(demoVideo?.video_url) && !demoVideo?.video_url.includes('vimeo');

  return (
    <div className="min-h-screen honeycomb-bg flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Two Column Layout - Vertically Centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          
          {/* Left Column - Demo Video + Feature Cards */}
          <div className="hidden md:flex flex-col gap-6">
            {demoVideo ? (
              <div className="relative">
                {/* Video Thumbnail Card */}
                <div 
                  onClick={() => setShowVideoModal(true)}
                  className="relative cursor-pointer group rounded-2xl overflow-hidden shadow-2xl border border-stone-200 bg-stone-900 aspect-video"
                  data-testid="demo-video-card"
                >
                  {thumbnail ? (
                    <img 
                      src={thumbnail} 
                      alt={demoVideo.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-600 to-amber-800">
                      <img src={appSettings.app_logo ? `${API}/api/uploads/${appSettings.app_logo}` : '/thrive-logo.png'} alt="Thrive" className="w-24 h-24 object-contain" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="bg-white/95 rounded-full p-5 shadow-xl transform group-hover:scale-110 transition-transform">
                      <Play className="w-10 h-10 text-amber-600 fill-amber-600" />
                    </div>
                  </div>
                  
                  {/* Duration Badge */}
                  {demoVideo.duration && (
                    <div className="absolute bottom-3 right-3 bg-black/80 text-white px-3 py-1 rounded-lg text-sm font-medium">
                      {demoVideo.duration}
                    </div>
                  )}
                </div>
                
                {/* Video Title & Description */}
                <div className="mt-4 text-center">
                  <h3 className="text-xl font-bold text-stone-800">{demoVideo.title}</h3>
                  {demoVideo.description && (
                    <p className="text-stone-600 mt-1">{demoVideo.description}</p>
                  )}
                  <p className="text-amber-600 mt-2 text-sm font-medium">Click to watch</p>
                </div>
              </div>
            ) : (
              // Placeholder when no video — use App Settings logo or fallback
              <div className="rounded-2xl overflow-hidden shadow-xl border border-stone-200 bg-white aspect-video flex flex-col items-center justify-center p-6">
                <img 
                  src={appSettings.app_logo ? `${API}/api/uploads/${appSettings.app_logo}` : '/thrive-logo.png'} 
                  alt="Thrive Beekeeping" 
                  className="max-h-52 max-w-[85%] object-contain"
                />
                <p className="text-amber-600 mt-4 text-xs font-medium">How-to video coming soon</p>
              </div>
            )}

            {/* Feature Cards — under the video */}
            <div className="grid grid-cols-2 gap-3">
              <Link to="/find-honey" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-find-honey-btn">
                <img src="/card-find-honey.jpg" alt="Find Local Honey" className="w-full h-24 object-cover" />
                <div className="p-2.5 text-center">
                  <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Find Local Honey</span>
                </div>
              </Link>
              <Link to="/report-swarm" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-report-swarm-btn">
                <img src="/card-report-swarm.jpg" alt="Report a Swarm" className="w-full h-24 object-cover" />
                <div className="p-2.5 text-center">
                  <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Report a Swarm</span>
                </div>
              </Link>
              <Link to="/advertise" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-advertise-btn">
                <img src="/card-advertise.jpg" alt="Advertise" className="w-full h-24 object-cover" />
                <div className="p-2.5 text-center">
                  <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Advertise</span>
                </div>
              </Link>
              <Link to="/find-bee-club" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-find-club-btn">
                <img src="/card-find-club.jpg" alt="Find a Bee Club" className="w-full h-24 object-cover" />
                <div className="p-2.5 text-center">
                  <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Find a Bee Club</span>
                </div>
              </Link>
            </div>
          </div>
          
          {/* Right Column - Login Form */}
          <div className="w-full max-w-md mx-auto md:mx-0">
            {/* Login Form Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-200">
              {/* Header — Logo centred */}
              <div className="flex justify-center mb-4">
                {appSettings.app_logo ? (
                  <img src={`${API}/api/uploads/${appSettings.app_logo}`} alt={appSettings.app_name || 'Thrive'} style={{ maxHeight: `${appSettings.logo_login_height || 80}px` }} className={`object-contain ${appSettings.logo_shape === 'circle' ? 'rounded-full' : appSettings.logo_shape === 'rounded' ? 'rounded-xl' : ''}`} />
                ) : (
                  <img src="/thrive-logo.png" alt="Thrive" className="h-16 object-contain" />
                )}
              </div>
              <p className="text-stone-600 text-sm mb-5 leading-relaxed text-center">
                {appSettings.app_description || 'Track your hives, inspections, treatments and harvests — all in one place. Built by beekeepers, for beekeepers.'}
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full"
                    disabled={loading}
                    data-testid="login-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full"
                    disabled={loading}
                    data-testid="login-password-input"
                  />
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="text-xs text-amber-600 hover:text-amber-700 hover:underline font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* CAPTCHA */}
                {captchaQuestion && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-amber-800">Verify you're human</label>
                      <button type="button" onClick={loadCaptcha} className="text-[10px] text-amber-600 hover:text-amber-800 underline">New question</button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-amber-900 bg-white px-3 py-1.5 rounded border border-amber-200 font-mono tracking-wide select-none" data-testid="captcha-question">{captchaQuestion}</span>
                      <Input
                        type="number"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        placeholder="?"
                        className="w-20 text-center font-bold text-lg"
                        disabled={loading}
                        data-testid="captcha-answer-input"
                      />
                    </div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              {/* Create Account CTA */}
              <div className="mt-6 pt-6 border-t border-stone-200">
                <div className="text-center mb-3">
                  <p className="text-sm text-stone-600">New to beekeeping management?</p>
                </div>
                <Link to="/trial-signup" data-testid="signup-link">
                  <Button 
                    variant="outline" 
                    className="w-full border-2 border-amber-400 text-amber-700 hover:bg-amber-50 hover:border-amber-500 font-semibold"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Free Account
                  </Button>
                </Link>
                <p className="text-center text-xs text-stone-500 mt-2">
                  {appSettings.free_platform
                    ? 'All features included — completely free, forever'
                    : 'Manage up to 2 hives free — no credit card required'}
                </p>
              </div>

              {/* Bottom Links */}
              <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between flex-wrap gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-stone-500 hover:text-amber-700 hover:bg-amber-50 text-xs"
                  onClick={handleDemoMode}
                  data-testid="demo-mode-button"
                >
                  👀 View Demo
                </Button>
                <Link 
                  to="/crew-login" 
                  className="inline-flex items-center text-xs text-stone-500 hover:text-amber-700 transition-colors"
                  data-testid="crew-login-link"
                >
                  <Users className="w-3 h-3 mr-1" />
                  Crew Login
                </Link>
                {!appSettings.free_platform && (
                <Link 
                  to="/pricing" 
                  className="inline-flex items-center text-xs text-stone-500 hover:text-amber-700 transition-colors"
                  data-testid="view-plans-link"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  View Plans
                </Link>
                )}
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-xs text-stone-400 mt-4">
              <a href={`mailto:${appSettings.contact_email || 'thrivebeekeeping@gmail.com'}`} className="hover:text-amber-600">{appSettings.contact_email || 'thrivebeekeeping@gmail.com'}</a>
            </p>
          </div>
        </div>
        
        {/* Mobile Demo Video - Below Form */}
        {demoVideo && (
          <div className="md:hidden mt-8">
            <div 
              onClick={() => setShowVideoModal(true)}
              className="relative cursor-pointer group rounded-xl overflow-hidden shadow-lg border border-stone-200 bg-stone-900 aspect-video"
              data-testid="demo-video-card-mobile"
            >
              {thumbnail ? (
                <img 
                  src={thumbnail} 
                  alt={demoVideo.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-600 to-amber-800">
                  <img src={appSettings.app_logo ? `${API}/api/uploads/${appSettings.app_logo}` : '/thrive-logo.png'} alt="Thrive" className="w-16 h-16 object-contain" />
                </div>
              )}
              
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="bg-white/95 rounded-full p-4 shadow-lg">
                  <Play className="w-8 h-8 text-amber-600 fill-amber-600" />
                </div>
              </div>
              
              {demoVideo.duration && (
                <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-0.5 rounded text-xs">
                  {demoVideo.duration}
                </div>
              )}
            </div>
            <p className="text-center text-sm text-stone-600 mt-2">{demoVideo.title}</p>
          </div>
        )}

        {/* Mobile Feature Cards */}
        <div className="grid grid-cols-2 gap-3 mt-8 md:hidden">
          <Link to="/find-honey" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-find-honey-btn-mobile">
            <img src="/card-find-honey.jpg" alt="Find Local Honey" className="w-full h-20 object-cover" />
            <div className="p-2 text-center">
              <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Find Local Honey</span>
            </div>
          </Link>
          <Link to="/report-swarm" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-report-swarm-btn-mobile">
            <img src="/card-report-swarm.jpg" alt="Report a Swarm" className="w-full h-20 object-cover" />
            <div className="p-2 text-center">
              <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Report a Swarm</span>
            </div>
          </Link>
          <Link to="/advertise" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-advertise-btn-mobile">
            <img src="/card-advertise.jpg" alt="Advertise" className="w-full h-20 object-cover" />
            <div className="p-2 text-center">
              <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Advertise</span>
            </div>
          </Link>
          <Link to="/find-bee-club" className="bg-white rounded-xl border border-amber-200 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all group" data-testid="login-find-club-btn-mobile">
            <img src="/card-find-club.jpg" alt="Find a Bee Club" className="w-full h-20 object-cover" />
            <div className="p-2 text-center">
              <span className="text-sm font-bold text-stone-700 group-hover:text-amber-700">Find a Bee Club</span>
            </div>
          </Link>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && demoVideo && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
          data-testid="video-modal"
        >
          <div 
            className="relative w-full max-w-4xl aspect-video bg-black rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors z-10"
              data-testid="video-modal-close"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video Player */}
            {isDirectVideo ? (
              <video 
                src={demoVideo.video_url} 
                controls 
                autoPlay 
                className="w-full h-full"
              />
            ) : embedUrl ? (
              <iframe
                src={embedUrl}
                title={demoVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white">
                <p>Video unavailable</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowForgot(false); setForgotSent(false); }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="forgot-password-modal">
            {forgotSent ? (
              <div className="text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-2">Check Your Email</h3>
                <p className="text-sm text-stone-500 mb-4">
                  If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link. Check your inbox (and spam folder).
                </p>
                <Button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="w-full bg-amber-600 hover:bg-amber-700">
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-stone-800 mb-1">Forgot Password?</h3>
                <p className="text-sm text-stone-500 mb-4">Enter your email and we'll send you a link to reset your password.</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-12"
                    data-testid="forgot-email-input"
                    autoFocus
                    required
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowForgot(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={forgotLoading} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="forgot-submit-btn">
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
